import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';

import { CommonCarousel } from '~/components/CommonCarousel';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import type { Category } from '~/stores/CategoriesStore';

import CategoryBlockTitle from '../CategoryBlockTitle';
import { cfSrcForImage, createCategoryUrl } from '../utils';

import styles from './CategoriesTilesXSScrollable.module.scss';

interface CategoriesTilesXSScrollableProps {
  category: Category;
  level?: number;
}

const CategoriesTilesXSScrollable = ({
  category,
  level = 2,
}: CategoriesTilesXSScrollableProps) => {
  const { handleClickCategory } = useCategoryAnalytics();

  const subcategories = (() => {
    if (level === 3) {
      const allChildren: Category[] = [];

      category.subcategory?.forEach((item) => {
        allChildren.push(...item.subcategory);
      });

      return allChildren;
    }

    return category.subcategory ?? [];
  })();

  if (!subcategories.length || !subcategories[0]?.id) {
    return null;
  }

  return (
    <div className={styles.container}>
      <CategoryBlockTitle
        text={category.name}
        className={styles.title}
        categoryId={category.id}
      />
      <CommonCarousel
        sliderClassName={styles.items}
        slideClassName={styles.item}
        spaceBetween={8}
        freeMode
        slidesPerViewProp="auto"
      >
        {subcategories.map(({ name, previewImage, id, previewText }) => {
          const imageUrl = cfSrcForImage(previewImage, {
            width: 285,
            height: 380,
          });

          return (
            <Link
              data-link={`category-${id}`}
              onClick={handleClickCategory(id)}
              to={createCategoryUrl({ id })}
              className={styles.link}
              key={`category-${id}`}
            >
              <div className={styles.item}>
                <div className={styles.imageBlock}>
                  {previewImage ? (
                    <img src={imageUrl} alt={previewText || name} />
                  ) : (
                    <div className={styles.emptyImg} />
                  )}
                  <div className={styles.gradient} />
                </div>
                <div className={styles.itemTitle}>
                  <span>{previewText || name}</span>
                </div>
              </div>
            </Link>
          );
        })}
      </CommonCarousel>
    </div>
  );
};

export default observer(CategoriesTilesXSScrollable);
